import * as React from 'react';
import { HcpGroup } from '@watcherr/resources';
import type { HcpGroupEntryProps, ZoneSummary } from '@watcherr/resources';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Select, Button } from '@watcherr/ui';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { useSite } from '~/hooks/site';
import { useZones } from '~/hooks/zone';

export const ZoneAssignmentTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { notifications, auth } = useStores();
  const [selectedPlane, setSelectedPlane] = React.useState<string>('');

  // Fetch site to get planes
  const site = useSite(auth.user?.site?.uuid);
  const planeOptions = React.useMemo(() => site?.planes?.map((p) => ({ value: p.uuid, label: p.name })) || [], [site]);

  // Fetch all groups
  const { data: groups } = useQuery<HcpGroupEntryProps[]>({
    queryKey: ['hcpGroups'],
    queryFn: () => HcpGroup.getHcpGroups(),
  });

  // Fetch zones, optionally filtered by plane
  const allZonesData = useZones({
    siteUuid: auth.user?.site?.uuid,
    planeUuid: selectedPlane || undefined,
  });

  // Transform ZoneEntry[] to ZoneSummary[]
  const allZones = React.useMemo<ZoneSummary[]>(() => {
    return allZonesData.map((zone) => ({
      id: zone.uuid,
      name: zone.name,
    }));
  }, [allZonesData]);

  // Mutation to assign zone to group
  const assignMutation = useMutation({
    mutationFn: ({ groupId, zoneId }: { groupId: string; zoneId: string }) =>
      HcpGroup.assignZoneToHcpGroup(groupId, zoneId),
    onSuccess: (updatedGroup) => {
      // Update the cache with the returned group data
      queryClient.setQueryData<HcpGroupEntryProps[]>(['hcpGroups'], (old) => {
        if (!old) return old;
        return old.map((group) => (group.id === updatedGroup.id ? updatedGroup : group));
      });
      notifications.addUserActionNotification({
        notificationType: 'success',
        message: t('hcpGroups.zone.assign.success'),
      });
    },
  });

  // Mutation to remove zone from group
  const removeMutation = useMutation({
    mutationFn: ({ groupId, zoneId }: { groupId: string; zoneId: string }) =>
      HcpGroup.unassignZoneFromHcpGroup(groupId, zoneId),
    onSuccess: (updatedGroup) => {
      // Update the cache with the returned group data
      queryClient.setQueryData<HcpGroupEntryProps[]>(['hcpGroups'], (old) => {
        if (!old) return old;
        return old.map((group) => (group.id === updatedGroup.id ? updatedGroup : group));
      });
      notifications.addUserActionNotification({
        notificationType: 'success',
        message: t('hcpGroups.zone.remove.success'),
      });
    },
    onError: () => {
      notifications.addUserActionNotification({
        notificationType: 'error',
        message: t('hcpGroups.zone.remove.error'),
      });
    },
  });

  return (
    <div>
      {planeOptions.length > 0 && (
        <div style={{ marginBottom: '20px', maxWidth: '300px' }}>
          <Select
            label={t('hcpGroups.filter.byFloor')}
            value={selectedPlane}
            onChange={setSelectedPlane}
            as="filter"
            options={[{ value: '', label: t('hcpGroups.filter.allFloor') }, ...planeOptions]}
            placeholder={t('hcpGroups.filter.allFloor')}
          />
        </div>
      )}
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeadCol>{t('hcpGroups.table.groupName')}</Table.HeadCol>
            <Table.HeadCol>{t('hcpGroups.assigned.zones')}</Table.HeadCol>
            <Table.HeadCol>{t('hcpGroups.add.zone')}</Table.HeadCol>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {Array.isArray(groups) &&
            groups.map((group) => (
              <Table.Row key={group.id}>
                <Table.Col>{group.hcpGroupName}</Table.Col>
                <Table.Col>
                  <GroupZones
                    groupId={group.id}
                    assignedZones={group.zones || []}
                    onRemove={(zoneId) => removeMutation.mutate({ groupId: group.id, zoneId })}
                    allZones={allZones}
                  />
                </Table.Col>
                <Table.Col>
                  <ZoneSelectorWithAddAll
                    groupId={group.id}
                    allZones={allZones}
                    assignedZones={group.zones || []}
                    onAssign={(zoneId) => assignMutation.mutate({ groupId: group.id, zoneId })}
                    onAssignAll={(zoneIds) => {
                      zoneIds.forEach((zoneId) => assignMutation.mutate({ groupId: group.id, zoneId }));
                    }}
                  />
                </Table.Col>
              </Table.Row>
            ))}
        </Table.Body>
      </Table>
    </div>
  );
};

// Display assigned zones with remove buttons
function GroupZones({
  groupId,
  assignedZones,
  onRemove,
  allZones,
}: {
  groupId: string;
  assignedZones: ZoneSummary[];
  onRemove: (zoneId: string) => void;
  allZones: ZoneSummary[];
}) {
  // Filter assigned zones to only show those in allZones (filtered by plane)
  const filteredZones = React.useMemo(() => {
    const allZoneIds = new Set(allZones.map((z) => z.id));
    return assignedZones.filter((zone) => allZoneIds.has(zone.id));
  }, [assignedZones, allZones]);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
      {filteredZones.length === 0 ? (
        <span style={{ color: '#999' }}>{t('hcpGroups.no.zones')}</span>
      ) : (
        filteredZones.map((zone) => (
          <div
            key={zone.id}
            style={{
              padding: '4px 12px',
              background: '#fff3e0',
              borderRadius: '16px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>{zone.name}</span>
            <button
              onClick={() => onRemove(zone.id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                fontSize: '16px',
              }}
              title={t('hcpGroups.zone.remove.label')}
            >
              ×
            </button>
          </div>
        ))
      )}
    </div>
  );
}

// Zone selector dropdown with Add All button
function ZoneSelectorWithAddAll({
  groupId,
  allZones,
  assignedZones,
  onAssign,
  onAssignAll,
}: {
  groupId: string;
  allZones: ZoneSummary[];
  assignedZones: ZoneSummary[];
  onAssign: (zoneId: string) => void;
  onAssignAll: (zoneIds: string[]) => void;
}) {
  const [selectedZone, setSelectedZone] = React.useState<string>('');

  // Get zones not yet assigned to this group (shared logic)
  const assignedZoneIds = new Set(assignedZones.map((z) => z.id));
  const availableZones = allZones.filter((zone) => !assignedZoneIds.has(zone.id));

  const handleChange = (value: string) => {
    if (value) {
      onAssign(value);
      setSelectedZone('');
    }
  };

  const handleAddAll = () => {
    const zoneIds = availableZones.map((zone) => zone.id);
    if (zoneIds.length > 0) {
      onAssignAll(zoneIds);
    }
  };

  const options = [
    { value: '', label: t('hcpGroups.select.zone') },
    ...availableZones.map((zone) => ({ value: zone.id, label: zone.name })),
  ];

  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
      <Select value={selectedZone} onChange={handleChange} options={options} placeholder={t('hcpGroups.select.zone')} />
      <Button
        onClick={handleAddAll}
        icon="plus"
        disabled={availableZones.length === 0}
        style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {t('hcpGroups.addAll.button')}
      </Button>
    </div>
  );
}
